// "Contact support" (PMU-288): in the parent app's Settings, the staff app's Me, and the console's
// account menu. Ask for help, see your requests and Vavr support's answers, write back. What the
// app was doing (version, screen, the last errors) goes with a new request; nothing about children.
import { SupportAccess } from "./ViewAccess.tsx";
import { useState } from "react";
import { Badge, Banner, Button, Card, Checkbox, Dialog, DialogClose, EmptyState, TextField, type BadgeTone } from "@vavr/ui";
import { api, ApiError, appInfo, blobData, dateTime, recentErrors, useAsync } from "../shared/index.ts";
import "./support.css";

interface TicketSummary { id: string; number: number; subject: string; status: string; created_at: number; updated_at: number; last_staff_at: number | null; last_customer_at: number | null }
interface Thread { ticket: { id: string; number: number; subject: string; status: string }; messages: { id: string; ts: number; author_kind: string; author_name: string | null; body: string }[]; files: { id: string; message_id: string | null; filename: string; size: number }[] }

const STATUS: Record<string, [string, BadgeTone]> = {
  new: ["Sent", "info"], open: ["Being looked at", "info"], waiting: ["Answered", "success"], resolved: ["Solved", "neutral"], closed: ["Closed", "neutral"],
};
const unseenAnswer = (t: TicketSummary) => t.status === "waiting" && (t.last_staff_at ?? 0) > (t.last_customer_at ?? 0);
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Could not reach Vavr. Check your connection.");

function NewRequest({ onSent }: { onSent: () => void }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ subject: "", body: "", urgent: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<number | null>(null);
  const send = async () => {
    setBusy(true); setError(null);
    try {
      const info = appInfo();
      const r = await api<{ number: number }>("POST", "/api/v1/support/tickets", {
        subject: f.subject.trim(), body: f.body.trim(), urgent: f.urgent,
        context: { app: info.app, version: info.version, screen: typeof location !== "undefined" ? location.pathname : undefined, device: typeof navigator !== "undefined" ? navigator.userAgent : undefined, errors: recentErrors() },
      });
      setSent(r.number); setF({ subject: "", body: "", urgent: false }); onSent();
    } catch (e) { setError(message(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog title={sent ? "Sent to Vavr support" : "Ask Vavr support"} description={sent ? undefined : "We answer here and by email, usually within a working day."}
      open={open} onOpenChange={(o) => { setOpen(o); if (o) { setSent(null); setError(null); } }}
      trigger={<Button icon="message">Ask for help</Button>}
      actions={sent ? <DialogClose><Button>Done</Button></DialogClose> : <><DialogClose><Button variant="secondary">Cancel</Button></DialogClose><Button loading={busy} disabled={f.subject.trim().length < 3 || f.body.trim().length < 3} onClick={send}>Send</Button></>}
    >
      {sent ? <p>{`Your request is T-${sent}. We will answer here and by email.`}</p> : (
        <div className="pmu-stack">
          {error && <Banner tone="danger" title={error} />}
          <Banner tone="info" title="If a child is in danger, call 911 first." />
          <TextField label="What is it about?" value={f.subject} maxLength={200} onChange={(e) => setF({ ...f, subject: e.target.value })} />
          <label className="pmu-field">
            <span className="pmu-field__label">What happened?</span>
            <textarea className="pmu-input pmu-textarea" rows={5} value={f.body} maxLength={20000} onChange={(e) => setF({ ...f, body: e.target.value })} />
          </label>
          <Checkbox label="It is stopping pickups or drop-offs right now" checked={f.urgent} onCheckedChange={(on) => setF({ ...f, urgent: !!on })} />
          <p className="pmu-small pmu-muted">We also get the app version and the last errors it saw, to find the problem faster. Never anything about children.</p>
        </div>
      )}
    </Dialog>
  );
}

/** The file goes up raw (the apps send their bearer token, the console its cookie). */
async function upload(ticketId: string, f: File) {
  if (f.size > 5 * 1024 * 1024) throw new ApiError(413, "too_large", "Files up to 5 MB.");
  await api("POST", `/api/v1/support/tickets/${ticketId}/files`, undefined, { raw: f, type: f.type || "application/octet-stream", headers: { "X-Filename": encodeURIComponent(f.name) } });
}
async function download(ticketId: string, f: { id: string; filename: string }) {
  const url = URL.createObjectURL(await blobData(`/api/v1/support/tickets/${ticketId}/files/${f.id}`));
  const a = document.createElement("a"); a.href = url; a.download = f.filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function ThreadDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const t = useAsync(() => api<Thread>("GET", `/api/v1/support/tickets/${id}`), [id]);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(null);
    try { await fn(); setReply(""); t.reload(); } catch (e) { setError(message(e)); } finally { setBusy(false); }
  };
  const d = t.data;
  const done = d && (d.ticket.status === "resolved" || d.ticket.status === "closed");
  return (
    <Dialog title={d ? `${d.ticket.subject} (T-${d.ticket.number})` : "Your request"} open onOpenChange={(o) => { if (!o) onClose(); }}
      actions={<>
        {d && !done && <Button variant="secondary" loading={busy} onClick={() => act(() => api("POST", `/api/v1/support/tickets/${id}/resolve`))}>It is solved</Button>}
        <Button loading={busy} disabled={!reply.trim()} onClick={() => act(() => api("POST", `/api/v1/support/tickets/${id}/messages`, { body: reply.trim() }))}>Send</Button>
      </>}
    >
      <div className="pmu-stack">
        {(error || t.error) && <Banner tone="danger" title={error ?? message(t.error)} />}
        {!d && !t.error && <p className="pmu-muted">Loading…</p>}
        {d && (
          <ol className="pmu-stack pmu-support-thread">
            {d.messages.map((m) => (
              <li key={m.id} className={`pmu-support-msg pmu-support-msg--${m.author_kind}`}>
                <span className="pmu-small pmu-muted">{m.author_name ?? "You"} · {dateTime(m.ts)}</span>
                <span className="pmu-support-msg__body">{m.body}</span>
                {d.files?.filter((f) => f.message_id === m.id).map((f) => <button key={f.id} type="button" className="pmu-link pmu-small" onClick={() => void download(id, f)}>{`📎 ${f.filename}`}</button>)}
              </li>
            ))}
          </ol>
        )}
        <label className="pmu-field">
          <span className="pmu-field__label">Add a screenshot or a file</span>
          <input type="file" accept="image/*,application/pdf,text/plain" disabled={busy} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void act(() => upload(id, f)); }} />
        </label>
        <label className="pmu-field">
          <span className="pmu-field__label">{done ? "Write again to reopen it" : "Your answer"}</span>
          <textarea className="pmu-input pmu-textarea" rows={3} value={reply} onChange={(e) => setReply(e.target.value)} />
        </label>
      </div>
    </Dialog>
  );
}

/** Help and support: the person's requests and a button to ask. `title` lets each app word it. */
export function SupportCard({ title = "Help and support" }: { title?: string }) {
  const list = useAsync(() => api<{ tickets: TicketSummary[] }>("GET", "/api/v1/support/tickets"), []);
  const [open, setOpen] = useState<string | null>(null);
  const tickets = list.data?.tickets ?? [];
  return (
    <Card title={title} description="Something not working, or a question? Vavr support answers here and by email." actions={<NewRequest onSent={list.reload} />}>
      {list.error ? <Banner tone="danger" title={message(list.error)} /> : tickets.length ? (
        <ul className="pmu-facts pmu-stack pmu-c-tight">
          {tickets.map((t) => {
            const [label, tone] = STATUS[t.status] ?? [t.status, "neutral" as BadgeTone];
            return (
              <li key={t.id}>
                <button type="button" className="pmu-support-row" onClick={() => setOpen(t.id)}>
                  <span className="pmu-stack pmu-c-tight"><strong>{t.subject}</strong><span className="pmu-small pmu-muted">{`T-${t.number} · ${dateTime(t.updated_at)}`}</span></span>
                  <Badge tone={unseenAnswer(t) ? "success" : tone}>{unseenAnswer(t) ? "New answer" : label}</Badge>
                </button>
              </li>
            );
          })}
        </ul>
      ) : list.data ? <p className="pmu-small pmu-muted">You have not asked us anything yet.</p> : null}
      {open && <ThreadDialog id={open} onClose={() => { setOpen(null); list.reload(); }} />}
    </Card>
  );
}

/** The console's page (account menu, "Help and support"). */
export function SupportPage() {
  return (
    <div className="pmu-stack">
      <SupportAccess />
      <SupportCard title="Your requests to Vavr support" />
      <EmptyState title="Email works too" description="Write to support@vavr.us from the address you sign in with, and it lands in the same place." />
    </div>
  );
}
